import { CircleDollarSign, LogIn, Play, Trophy, Wallet } from "lucide-react";
import { cva } from "class-variance-authority";

import type { LucideIcon } from "lucide-react";
import type { TaskSchema } from "@/types";
import { cn } from "@/lib/utils";

/**
 * THE task icon. Every campaign type that renders tasks (tournament point
 * rules, mission tasks, the in-game button pills) goes through this component,
 * so the kind → glyph decision lives in exactly one place. `title` is a
 * translated string, so `kind` (from the backend) drives the choice.
 */
const TASK_ICON: Record<TaskSchema["kind"], LucideIcon> = {
  play: Play,
  win: Trophy,
  turnover: CircleDollarSign,
  deposit: Wallet,
  login: LogIn,
};

/** Bare glyph sizes; stroke thins as the glyph grows (optical balance). */
const GLYPH = {
  xs: { className: "size-3", strokeWidth: 2 },
  sm: { className: "size-4.5", strokeWidth: 1.75 },
  md: { className: "size-4", strokeWidth: 2 },
  lg: { className: "size-6", strokeWidth: 1.75 },
} as const;

const chipVariants = cva(
  "flex shrink-0 items-center justify-center bg-primary/[0.14]",
  {
    variants: {
      size: {
        xs: "size-5 rounded-[6px]",
        sm: "size-6 rounded-[7px]",
        md: "size-7 rounded-[8px]",
        lg: "size-9 rounded-[10px]",
      },
    },
    defaultVariants: { size: "md" },
  }
);

interface TaskIconProps {
  kind: TaskSchema["kind"];
  /** `plain` = bare glyph (color via className); `chip` = tinted rounded
   *  square with a primary-colored glyph inside. */
  variant?: "plain" | "chip";
  size?: keyof typeof GLYPH;
  className?: string;
}

export const TaskIcon = ({
  kind,
  variant = "plain",
  size = "md",
  className,
}: TaskIconProps) => {
  const Icon = TASK_ICON[kind] ?? Play;
  const glyph = GLYPH[size];

  if (variant === "chip") {
    return (
      <span className={cn(chipVariants({ size }), className)}>
        <Icon
          className={cn(glyph.className, "text-primary")}
          strokeWidth={glyph.strokeWidth}
        />
      </span>
    );
  }

  return (
    <Icon
      className={cn(glyph.className, "shrink-0", className)}
      strokeWidth={glyph.strokeWidth}
    />
  );
};
